import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/format";
import { toast } from "sonner";
import { Gate, ready, Panel, useLoad, cad, stamp, Chip, Table, fieldStyle, Label } from "@/components/console/ui";
import { fmtDuration } from "@/components/SentenceClock";
import { RecordPayout } from "@/components/DrawPotModal";

function LiveClock({ s }) {
  const [now, setNow] = useState(Date.now());
  const [at] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const rem = s.active ? s.remaining_seconds - (now - at) / 1000 : s.remaining_seconds;
  return <span className="font-head text-5xl md:text-6xl tracking-widest" style={{ color: "var(--accent)" }} data-testid="console-clock-remaining">{fmtDuration(rem)}</span>;
}

function StartForm({ running, onDone }) {
  const [d, setD] = useState(""), [h, setH] = useState(""), [m, setM] = useState("");
  const [busy, setBusy] = useState(false);
  const minutes = (Number(d) || 0) * 1440 + (Number(h) || 0) * 60 + (Number(m) || 0);
  const start = async () => {
    if (minutes < 1 || busy) return;
    if (running && !window.confirm("restart the clock? the current countdown is replaced.")) return;
    setBusy(true);
    try { await api.post("/console/sentence", { action: "start", minutes }); toast.success("clock started"); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const num = (v, set, id) => <input data-testid={id} value={v} onChange={(e) => set(e.target.value.replace(/[^0-9]/g, ""))} placeholder="0" className="w-full px-3 py-2 text-lg font-head" style={fieldStyle} />;
  return (
    <div data-testid="sentence-start-form">
      <Label>set starting length</Label>
      <div className="grid grid-cols-3 gap-2">
        <div>{num(d, setD, "start-days")}<span className="text-[10px] opacity-60">days</span></div>
        <div>{num(h, setH, "start-hours")}<span className="text-[10px] opacity-60">hours</span></div>
        <div>{num(m, setM, "start-minutes")}<span className="text-[10px] opacity-60">minutes</span></div>
      </div>
      <button onClick={start} disabled={minutes < 1 || busy} data-testid="sentence-start-btn" className="btn w-full py-3 mt-4 text-xs tracking-widest">
        {busy ? "…" : `${running ? "restart" : "start"} · ${fmtDuration(minutes * 60)}`}
      </button>
    </div>
  );
}

export function SentenceControl() {
  const [s, reload] = useLoad("/sentence", 15000);
  const [log, reloadLog] = useLoad("/console/sentence/log");
  const [items] = useLoad("/console/payments?kind=sentence_item");
  const [busy, setBusy] = useState(false);
  const refresh = () => { reload(); reloadLog(); };
  const act = async (action) => {
    if (busy) return;
    if (action === "stop" && !window.confirm("stop the clock? remaining time is cleared.")) return;
    setBusy(true);
    try { await api.post("/console/sentence", { action }); toast.success(`clock ${action === "stop" ? "stopped" : action + "d"}`); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!ready(s)) return <Gate data={s} testId="sentence" />;
  const status = s.active ? "running" : s.paused ? "paused" : "stopped";
  return (
    <div className="flex flex-col gap-6" data-testid="console-sentence">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Panel className="lg:col-span-8" title="sentence clock" right={<Chip on={s.active} testId="sentence-status">{status}</Chip>} testId="sentence-clock-panel">
          <div className="flex flex-col items-center py-16 gap-3">
            <LiveClock key={`${s.remaining_seconds}-${status}`} s={s} />
            <span className="text-[10px] tracking-[0.3em] opacity-60">days : hours : minutes : seconds</span>
            <span className="text-xs mt-2 opacity-80" data-testid="console-clock-start">starting length · {fmtDuration(s.start_minutes * 60)}</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <button onClick={() => act("pause")} disabled={!s.active || busy} data-testid="sentence-pause-btn" className="btn py-3 text-xs tracking-widest">pause</button>
            <button onClick={() => act("resume")} disabled={!s.paused || busy} data-testid="sentence-resume-btn" className="btn py-3 text-xs tracking-widest">resume</button>
            <button onClick={() => act("stop")} disabled={(!s.active && !s.paused) || busy} data-testid="sentence-stop-btn" className="btn py-3 text-xs tracking-widest">stop</button>
          </div>
        </Panel>
        <div className="lg:col-span-4 flex flex-col gap-6">
          <Panel title="start / restart" testId="sentence-start-panel"><StartForm running={s.active || s.paused} onDone={refresh} /></Panel>
          <Panel title="the pot" testId="sentence-pot-panel">
            <span className="font-head text-3xl" style={{ color: "var(--accent)" }} data-testid="console-pot">{cad(s.pot_cad)} cad</span>
            <div className="-mt-4"><RecordPayout onUpdated={refresh} /></div>
          </Panel>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="help / punish purchases" testId="sentence-items-panel">
          <Table testId="sentence-items-table" rowKey="session_id" rows={items} empty="no help or punish purchases yet"
            cols={[{ k: "created_at", label: "when", render: (r) => stamp(r.created_at) }, { k: "user", label: "who" }, { k: "label", label: "item" }, { k: "amount", label: "cad", render: (r) => cad(r.amount) }]} />
        </Panel>
        <Panel title="clock & pot log" testId="sentence-log-panel">
          <Table testId="sentence-log-table" rows={!ready(log) ? log : [...log.actions.map((a) => ({ ...a, what: `${a.action}${a.minutes ? ` · ${fmtDuration(a.minutes * 60)}` : ""}` })), ...log.payouts.map((p) => ({ ...p, by: p.recorded_by, what: `payout · ${cad(p.amount)}` }))].sort((a, b) => (a.created_at < b.created_at ? 1 : -1))}
            empty="no clock actions yet" cols={[{ k: "created_at", label: "when", render: (r) => stamp(r.created_at) }, { k: "by", label: "member" }, { k: "what", label: "action" }]} />
        </Panel>
      </div>
    </div>
  );
}
